import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, BookOpen, Heart, ChevronRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DiscoveryPage = () => {
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [articlesRes, videosRes, storiesRes] = await Promise.all([
        axios.get(`${API}/discovery/articles`),
        axios.get(`${API}/discovery/videos`),
        axios.get(`${API}/discovery/stories`)
      ]);
      setArticles(articlesRes.data);
      setVideos(videosRes.data);
      setStories(storiesRes.data);
      
      // Set featured video
      const introVideo = videosRes.data.find(v => v.category === 'intro');
      if (introVideo) setSelectedVideo(introVideo);
      else if (videosRes.data.length > 0) setSelectedVideo(videosRes.data[0]);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    // Handle various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    // If already an embed URL or Vimeo, return as is
    if (url.includes('embed') || url.includes('vimeo')) {
      return url;
    }
    return url;
  };

  const categoryLabels = {
    news: 'News',
    insight: 'Insight',
    resource: 'Resource',
    intro: 'Introduction',
    educational: 'Educational',
    testimonial: 'Client Story'
  };

  // Sample content if database is empty
  const sampleArticles = [
    {
      id: 'sample-1',
      title: 'Understanding Trauma: How Past Experiences Shape Our Present',
      excerpt: 'Trauma affects us in ways we may not always recognise. Learn how psychoanalytic therapy can help you understand and process these experiences.',
      category: 'insight',
      image_url: 'https://images.unsplash.com/photo-1763060869007-fc10548b0aae?w=600',
      created_at: new Date().toISOString()
    },
    {
      id: 'sample-2',
      title: 'The Therapeutic Relationship: Why Connection Matters in Healing',
      excerpt: 'At the heart of psychoanalytic therapy is the relationship between therapist and client. Discover why this connection is so important.',
      category: 'insight',
      image_url: 'https://images.unsplash.com/photo-1660831219257-dfc4f9bc843f?w=600',
      created_at: new Date().toISOString()
    },
    {
      id: 'sample-3',
      title: 'Navigating Cultural Identity in Therapy',
      excerpt: 'How language, culture, and identity shape our emotional experiences and why multilingual therapy can make a difference.',
      category: 'resource',
      image_url: 'https://images.unsplash.com/photo-1759513332004-c264d9231152?w=600',
      created_at: new Date().toISOString()
    }
  ];

  const sampleVideos = [
    {
      id: 'sample-v1',
      title: 'Welcome to KBC Psychotherapy',
      description: 'An introduction to my practice and approach to psychoanalytic psychotherapy.',
      video_url: 'https://www.youtube.com/embed/MtD8JCxmFbs',
      category: 'intro',
      created_at: new Date().toISOString()
    },
    {
      id: 'sample-v2',
      title: 'What is Psychoanalytic Psychotherapy?',
      description: 'Understanding how psychoanalytic therapy differs from other approaches and what to expect.',
      video_url: 'https://www.youtube.com/embed/sRmhVWYf9zQ',
      category: 'educational',
      created_at: new Date().toISOString()
    }
  ];

  const sampleStories = [
    {
      id: 'sample-s1',
      title: 'Finding My Voice Again',
      content: 'After years of feeling silenced in my relationships, therapy helped me reconnect with parts of myself I had forgotten. The journey wasn\'t easy, but having a safe space to explore my experiences made all the difference.',
      author_name: 'Sarah',
      author_description: 'Client for 2 years',
      created_at: new Date().toISOString()
    },
    {
      id: 'sample-s2',
      title: 'Understanding My Patterns',
      content: 'I came to therapy struggling with the same issues appearing in every relationship. Through our work together, I began to understand how my childhood experiences were shaping my present. This understanding has been transformative.',
      author_name: 'Michael',
      author_description: 'Client for 18 months',
      created_at: new Date().toISOString()
    },
    {
      id: 'sample-s3',
      title: 'Processing Trauma at My Own Pace',
      content: 'What I appreciated most was never feeling rushed. The therapy provided a containing space where I could gradually face difficult memories without feeling overwhelmed. I finally feel like I\'m moving forward.',
      author_name: 'Amara',
      author_description: 'Client for 3 years',
      created_at: new Date().toISOString()
    }
  ];

  const displayArticles = articles.length > 0 ? articles : sampleArticles;
  const displayVideos = videos.length > 0 ? videos : sampleVideos;
  const displayStories = stories.length > 0 ? stories : sampleStories;
  const displaySelectedVideo = selectedVideo || (displayVideos.length > 0 ? displayVideos[0] : null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="discovery-page">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium tracking-wide uppercase text-accent mb-4">
              Discover
            </p>
            <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight mb-4">
              Insights & Resources
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore articles, videos, and stories about psychotherapy, mental health, 
              and the journey towards healing and self-understanding.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Video Section */}
      {displaySelectedVideo && (
        <section className="py-16" data-testid="featured-video-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-light mb-8">Featured Video</h2>
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-lg">
                    <iframe
                      src={getYouTubeEmbedUrl(displaySelectedVideo.video_url)}
                      title={displaySelectedVideo.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <h3 className="font-heading text-2xl mt-4">{displaySelectedVideo.title}</h3>
                  <p className="text-muted-foreground mt-2">{displaySelectedVideo.description}</p>
                </div>
                <div className="space-y-4">
                  <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">More Videos</h4>
                  {displayVideos.slice(0, 4).map((video) => (
                    <div
                      key={video.id}
                      onClick={() => setSelectedVideo(video)}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        displaySelectedVideo?.id === video.id
                          ? 'bg-primary/10 border-2 border-primary'
                          : 'bg-card hover:bg-secondary/50'
                      }`}
                      data-testid={`video-thumbnail-${video.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <Play className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm line-clamp-2">{video.title}</p>
                          <span className="text-xs text-muted-foreground">
                            {categoryLabels[video.category] || video.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Content Tabs */}
      <section className="py-16 bg-secondary/30" data-testid="content-tabs-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <Tabs defaultValue="articles" className="space-y-8">
            <TabsList className="bg-card rounded-xl p-1 w-full md:w-auto">
              <TabsTrigger value="articles" className="rounded-lg flex items-center gap-2" data-testid="tab-articles">
                <BookOpen className="h-4 w-4" />
                Articles
              </TabsTrigger>
              <TabsTrigger value="stories" className="rounded-lg flex items-center gap-2" data-testid="tab-stories">
                <Heart className="h-4 w-4" />
                Testimonials
              </TabsTrigger>
            </TabsList>

            {/* Articles Tab */}
            <TabsContent value="articles">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {displayArticles.map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card rounded-2xl overflow-hidden shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(42,90,82,0.12)] transition-all duration-500 group"
                    data-testid={`article-card-${article.id}`}
                  >
                    {article.image_url && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={article.image_url}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <span className="text-xs font-medium uppercase tracking-wide text-accent">
                        {categoryLabels[article.category] || article.category}
                      </span>
                      <h3 className="font-heading text-xl mt-2 mb-3 group-hover:text-primary transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-3">
                        {article.excerpt}
                      </p>
                      <Button variant="ghost" className="mt-4 p-0 h-auto text-primary">
                        Read More <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </motion.article>
                ))}
              </div>
            </TabsContent>

            {/* Stories Tab */}
            <TabsContent value="stories">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {displayStories.map((story, index) => (
                  <motion.div
                    key={story.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]"
                    data-testid={`story-card-${story.id}`}
                  >
                    <Quote className="h-8 w-8 text-accent/30 mb-4" />
                    <h3 className="font-heading text-xl mb-4">{story.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      "{story.content}"
                    </p>
                    <div className="border-t border-border pt-4">
                      <p className="font-medium">{story.author_name}</p>
                      {story.author_description && (
                        <p className="text-sm text-muted-foreground">{story.author_description}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl font-light text-primary-foreground mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              If you're considering therapy, the first step is often the hardest. 
              Book an initial consultation to see if we're a good fit.
            </p>
            <Link to="/book">
              <Button
                size="lg"
                variant="secondary"
                className="rounded-full px-8 py-6"
                data-testid="discovery-cta-btn"
              >
                Book Initial Consultation · £50
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DiscoveryPage;
